import Cache from '@fs/zion-cache'
import { mutate } from 'swr'
import type { Person, PersonId, PID } from '@fs/zion-tree-types'
import { useEffect } from 'react'
import { useGroupsContext } from '@fs/zion-tree-groups'
import { usePersonPageParams } from '../person-page/personPageRouteUtil'
import { useFocusPID } from '../providers/FocusPIDContext'
import { useShowTempleContext } from '../providers/ShowTempleContext'
import type { SwrResponse } from './useSwrRequest'
import { useSwrRequest } from './useSwrRequest'
import { mutateChangeLogSummary } from './useChangeLogSummary'
import { readPerson } from './emulators/personBE'

// We only need to set this once per page load, but we can't use a hook and provider to do it safely.
let isMemberLocalContext = false

const personsCache = new Cache({ storeName: 'persons_r9', encrypted: process.env.NODE_ENV === 'production' })
const swrKey = (pid: string, treeId: string, includeTemple: boolean): string => {
  const includeTempleQuery = includeTemple ? 'includeTempleRollupStatus=true' : undefined
  const queries = [includeTempleQuery, `enforceTemplePolicyEx=true`, `treeId=${treeId}`].filter(Boolean).join('&')
  return `/service/tree/tree-data/v8/person/${pid}/card?${queries}`
}

// The temple rollup status comes back as CALCULATING while the backend computes it. We poll until it
// resolves (or we hit ROLLUP_MAX_POLLS), on an escalating backoff that settles at the last wait. This
// is tracked per request key at the module level (not per hook instance) so the many components
// calling useFocusPerson for the same person drive a single shared poll instead of each firing its
// own, which floods the endpoint.
export const ROLLUP_POLL_WAITS_MS = [500, 1000, 3000, 5000, 8000]

// Give up after this many polls so a status that never resolves doesn't poll forever.
export const ROLLUP_MAX_POLLS = 15

const rollupPollTimers = new Map<string, ReturnType<typeof setTimeout>>()
const rollupPollCounts = new Map<string, number>()

function stopRollupPoll(key: string): void {
  const timeoutId = rollupPollTimers.get(key)
  if (timeoutId) clearTimeout(timeoutId)
  rollupPollTimers.delete(key)
  rollupPollCounts.delete(key)
}

function scheduleRollupPoll(key: string): void {
  // A poll is already pending for this key, so don't stack another (this is what dedupes the many
  // components sharing this key down to a single shared poll).
  if (rollupPollTimers.has(key)) return

  const pollCount = rollupPollCounts.get(key) ?? 0
  // Hit the cap — stop polling. The count stays set so a remount doesn't restart the loop; it is
  // cleared when the status finally resolves (stopRollupPoll).
  if (pollCount >= ROLLUP_MAX_POLLS) return
  const waitMs = ROLLUP_POLL_WAITS_MS[Math.min(pollCount, ROLLUP_POLL_WAITS_MS.length - 1)]

  const timeoutId = setTimeout(async () => {
    rollupPollTimers.delete(key)
    rollupPollCounts.set(key, pollCount + 1)
    try {
      const person = await mutate<Person & { id: PID }>(key)
      if (person?.templeRollupStatus === 'CALCULATING') {
        scheduleRollupPoll(key)
      } else {
        rollupPollCounts.delete(key)
      }
    } catch {
      // Stop polling on error. Like a normal resolve, this clears the count, so a deliberate
      // remount gets a fresh budget — an error isn't a definitive "still calculating" signal
      // (retry-axios has already exhausted its retries). The hook surfaces the error separately.
      stopRollupPoll(key)
    }
  }, waitMs)

  rollupPollTimers.set(key, timeoutId)
}

// Exported for tests to reset the module-level poll state between cases.
export function resetRollupPolls(): void {
  rollupPollTimers.forEach(clearTimeout)
  rollupPollTimers.clear()
  rollupPollCounts.clear()
}

export default function useFocusPerson(): SwrResponse<Person & { id: PID }> & {
  notFound: boolean
  focusPerson?: Person & { id: PID }
} {
  const { pid: paramsPid } = usePersonPageParams('full')
  const { currentTreeId } = useGroupsContext()

  // When useFocusPerson is used inside of a MemoryRouter, the route 'pid' param likely doesn't exist.
  // The FocusPIDContext provides it from a higher level that is still accessible inside the
  // MemoryRouter, so fall back to it for the request key.
  const focusPID = useFocusPID()
  const pid = paramsPid || focusPID
  const [showTemple, isTempleLoading] = useShowTempleContext()

  if (!isMemberLocalContext && showTemple) isMemberLocalContext = true

  const requestKey =
    isTempleLoading === 'loading' ? null : swrKey(pid, currentTreeId || 'PRIVATE', isMemberLocalContext)

  const response = useSwrRequest<Person & { id: PID }>(requestKey, personsCache, {
    dedupingInterval: 10000, // Dedupe fetching the person by 10 seconds
    focusThrottleInterval: 60 * 1000 * 2, // Only refetch the person when the tab gets refocused if more than 2 minutes has passed since last refetch
    emulator: readPerson,
  })

  const notFound =
    !!response.error?.response?.status && response.error.response.status >= 400 && response.error.response.status < 500

  // Poll the focus person while its temple rollup status is still being calculated by the backend.
  // The poll itself is deduped per key at the module level, so every component using this hook shares
  // one poll loop instead of each running its own.
  const rollupStatus = response.data?.templeRollupStatus
  useEffect(() => {
    if (!requestKey) return
    if (rollupStatus === 'CALCULATING') {
      scheduleRollupPoll(requestKey)
    } else if (rollupStatus) {
      stopRollupPoll(requestKey)
    }
  }, [requestKey, rollupStatus])

  return {
    ...response,
    notFound,
    focusPerson: response.data,
    isLoading: isTempleLoading === 'loading' || response.isLoading,
  }
}

export function mutateFocusPerson(pid: PersonId, treeId: string): void {
  mutate(swrKey(pid, treeId, isMemberLocalContext))
  mutateChangeLogSummary(pid, treeId)
}
